import { useCallback, useEffect, useMemo, useState } from 'react'
import { localStorageHelper } from '../../../../utils/localStorageHelper'

/**
 * Loads saved column config from localStorage. Validates that the saved
 * columns match the blueprint's columns by name. Returns null if invalid.
 */
function loadSavedColumns (storageKey, blueprintColumns) {
  if (!storageKey) return null

  const saved = localStorageHelper.tryLoad(storageKey)
  if (!Array.isArray(saved) || saved.length === 0) return null

  const blueprintNames = new Set(blueprintColumns.map(c => c.name))
  const savedNames = new Set(saved.map(c => c.name))

  if (blueprintNames.size !== savedNames.size) return null
  for (const name of blueprintNames) {
    if (!savedNames.has(name)) return null
  }

  return saved
}

/**
 * Shared hook for user-configurable column visibility and ordering.
 *
 * Manages column preferences (order + hidden state) with optional
 * localStorage persistence. Fresh blueprint metadata (displayName,
 * align, formatString, sortable, etc.) always comes from the
 * blueprint config; only order and hidden come from saved prefs.
 *
 * @param {Array} configColumns - columns from the blueprint presentation config
 * @param {string} [localStorageKey] - optional key for persisting prefs
 * @returns {{ mergedColumns: Array, handleColumnConfigSave: Function }}
 */
export default function useColumnConfig (configColumns, localStorageKey) {
  const storageKey = localStorageKey ? `${localStorageKey}.columns` : null

  const [userColumnPrefs, setUserColumnPrefs] = useState(() => {
    return loadSavedColumns(storageKey, configColumns)
  })

  const columnKey = useMemo(
    () => (configColumns || []).map(c => c.name).sort().join(','),
    [configColumns]
  )

  useEffect(() => {
    setUserColumnPrefs(loadSavedColumns(storageKey, configColumns))
  }, [storageKey, columnKey, configColumns])

  const mergedColumns = useMemo(() => {
    if (!userColumnPrefs) return configColumns

    const blueprintMap = new Map(configColumns.map(c => [c.name, c]))

    return userColumnPrefs.map(saved => {
      const fresh = blueprintMap.get(saved.name)
      if (!fresh) return saved
      return { ...fresh, hidden: saved.hidden }
    })
  }, [configColumns, userColumnPrefs])

  const handleColumnConfigSave = useCallback((updatedColumns) => {
    setUserColumnPrefs(updatedColumns)
    if (storageKey) localStorageHelper.store(storageKey, updatedColumns)
  }, [storageKey])

  return { mergedColumns, handleColumnConfigSave }
}
